import vm, { JourneyNode, JourneyLine, UngappedJourney } from '@journeys/Journeys.viewmodel';
import { computed, ComputedRef, toRaw } from 'vue';
import { NodeType } from '@journeys/Enums';
import GraphData from '@journeys/components/GraphData';
import { deepClone } from '@shared/utils';

const { editor, activeJourney } = vm;

/**
 * Cleans up orphaned references in journey data to ensure data integrity.
 * This fixes issues where:
 * - Nodes reference lines that don't exist (in lnto/lnfr)
 * - Lines reference nodes that don't exist (in to/fr)
 * @param journey The journey data to clean up
 * @returns Object with counts of cleaned up references
 */
export const cleanupOrphanedReferences = (journey: UngappedJourney) => {
    const stats = {
        removedLineRefsFromNodes: 0,
        removedNodeRefsFromLines: 0,
        removedOrphanedLines: 0,
    };

    // Step 1: Clean up invalid line references from nodes' lnto and lnfr
    for (const nodeId in journey.nodes) {
        const node = journey.nodes[nodeId];

        // Skip text nodes (they don't have lnto/lnfr)
        if (node.data.type === NodeType.Text) continue;

        const journeyNode = node as JourneyNode;

        // Clean lnto (lines going out from this node)
        if (journeyNode.lnto) {
            for (const lineId in journeyNode.lnto) {
                if (!journey.lines[lineId]) {
                    delete journeyNode.lnto[lineId];
                    stats.removedLineRefsFromNodes++;
                }
            }
        }

        // Clean lnfr (lines coming into this node)
        if (journeyNode.lnfr) {
            for (const lineId in journeyNode.lnfr) {
                if (!journey.lines[lineId]) {
                    delete journeyNode.lnfr[lineId];
                    stats.removedLineRefsFromNodes++;
                }
            }
        }
    }

    // Step 2: Clean up invalid node references from lines and remove orphaned lines
    const linesToRemove: string[] = [];

    for (const lineId in journey.lines) {
        const line = journey.lines[lineId] as JourneyLine;

        // Check if 'to' node exists
        if (line.to !== null && !journey.nodes[line.to]) {
            line.to = null;
            stats.removedNodeRefsFromLines++;
        }

        // Check if 'fr' node exists
        if (line.fr !== null && !journey.nodes[line.fr]) {
            line.fr = null;
            stats.removedNodeRefsFromLines++;
        }

        // Mark completely orphaned lines (no valid connections) for removal
        if (line.to === null && line.fr === null) {
            linesToRemove.push(lineId);
        }
    }

    // Remove completely orphaned lines
    for (const lineId of linesToRemove) {
        delete journey.lines[lineId];
        stats.removedOrphanedLines++;
    }

    if (stats.removedLineRefsFromNodes > 0 || stats.removedNodeRefsFromLines > 0 || stats.removedOrphanedLines > 0) {
        console.warn('[Journey] Cleaned up orphaned references:', stats);
    }

    return stats;
};

export const buildGraph = (list: any) => {
    editor.segmentGraph.setList(list, activeJourney.startingNode!);
    editor.segmentGraph.sequenceSteps();

    activeJourney.steps = editor.segmentGraph.steps;
    activeJourney.startingStep = editor.segmentGraph.startingStep;

    return editor.segmentGraph.steps;
};

export const getConnections = (obj: any, dir: 'to' | 'fr', activeJourney: any) =>
    Object.values(obj).reduce((acc: number[], c: any) => {
        const line = activeJourney.lines[c.id];
        if (!line) return acc;

        const line_conn = line[dir];
        line_conn && acc.push(line_conn);

        return acc;
    }, []);

export const serializeStepSegments = () => {
    if (!activeJourney.startingNode) return;

    const adj_list = new Map(),
        queue_stack: number[] = [],
        newPath = (path_nr: number = path_cnt + 1) => {
            let enum_cnt = [];

            // enum_cnt[NodeType.Placeholder] = 0
            enum_cnt[0] = 0;
            enum_cnt[NodeType.Task] = 0;
            enum_cnt[NodeType.Segment] = 0;
            enum_cnt[NodeType.Condition] = 0;

            return {
                nr: path_nr,
                enum_cnt,
                nodes: {},
            };
        };

    let visited: any = {},
        path_cnt = 0,
        counter: any = [newPath()];

    /**
     * recursive function to serialize node relations
     * @param cid {number} current id
     */
    const nextNode = (cid: number) => {
        if (visited[cid]) {
            queue_stack[0] ? nextNode(queue_stack.shift()!) : buildGraph(adj_list);

            return;
        }

        visited[cid] = true;

        let node = activeJourney.nodes[cid] as JourneyNode,
            npd = {
                path: counter[path_cnt].nr,
                type_nr: ++counter[path_cnt].enum_cnt[node.data.type as 1],
            };

        // node.runtime.ordinal ||= computed(() => {
        // 	let no = `${npd.path}.${npd.type_nr}`

        // 	switch (node.data.type) {
        // 		case NodeType.Task:
        // 			return `M${no}`
        // 		case NodeType.Segment:
        // 			return `L${no}`
        // 		default:
        // 			return
        // 	}
        // })

        let conns_to = getConnections(node.lnto, 'to', activeJourney),
            conns_fr = getConnections(node.lnfr, 'fr', activeJourney),
            edge_data = {
                node,
                to: conns_to,
                fr: conns_fr,
                path: npd.path,
                type_nr: npd.type_nr,
            };

        adj_list.set(cid, edge_data);

        let ct = conns_to.reduce((a: number[], c: number) => (!visited[c] && a.push(c), a), []);

        if (ct.length > 0) {
            let next = ct.shift();
            queue_stack.push(...ct);
            nextNode(next!);
        } else if (queue_stack[0]) {
            counter[++path_cnt] = newPath();
            nextNode(queue_stack.shift()!);
        } else {
            // end recursion
            Object.keys(activeJourney.nodes).forEach((key: string) => {
                let node = activeJourney.nodes[key];

                if (node.data.type !== NodeType.Text) {
                    node = node as JourneyNode;

                    if (!visited[key]) delete node.runtime?.ordinal;
                }
            });

            buildGraph(adj_list);
        }
    };

    nextNode(activeJourney.startingNode!);
};

export const createStepList = (): ComputedRef<any[] | null> => {
    return computed(() => {
        let steps: any[] = Object.values(activeJourney.steps);

        if (typeof steps[0] !== 'object') {
            serializeStepSegments();
            steps = Object.values(activeJourney.steps);
        }

        return steps.sort((a: any, b: any) => a.delay - b.delay);
    });
};

export const stepList: ComputedRef<any[] | null> = createStepList();

/**
 * Finds every ancestor of the target node - i.e. every node that lies on ANY path from the
 * journey's starting node to it, across ALL of its incoming branches - and builds an adjacency
 * list scoped to just those nodes, so a temporary graph traversal can be run in isolation
 * without affecting the rest of the journey. A node reached via more than one branch (a merge
 * point) needs every branch represented here, or a downstream traversal only ever sees
 * whichever single branch happened to be picked.
 */
const buildAncestorAdjacencyList = (targetNodeId: number): { adj_list: Map<number, any> } | null => {
    if (!activeJourney.startingNode) return null;

    // Walk backwards from the target, collecting every node that can reach it.
    // Uses a read pointer instead of Array.shift() - shift() is O(n) per call, which would
    // make this BFS O(n^2) for a journey with n ancestor nodes.
    const ancestorIds = new Set<number>();
    const queue: number[] = [targetNodeId];
    let queueHead = 0;

    while (queueHead < queue.length) {
        const nodeId = queue[queueHead++];
        if (ancestorIds.has(nodeId)) continue;
        ancestorIds.add(nodeId);

        const node = activeJourney.nodes[nodeId] as JourneyNode;
        if (!node) continue;

        for (const prevId of getConnections(node.lnfr, 'fr', activeJourney)) {
            if (!ancestorIds.has(prevId)) queue.push(prevId);
        }
    }

    if (!ancestorIds.has(activeJourney.startingNode)) {
        return null;
    }

    // Build adjacency list only for the collected ancestor nodes
    const adj_list = new Map();

    for (const nodeId of ancestorIds) {
        const node = activeJourney.nodes[nodeId] as JourneyNode;
        const conns_to = getConnections(node.lnto, 'to', activeJourney).filter((id) => ancestorIds.has(id));
        const conns_fr = getConnections(node.lnfr, 'fr', activeJourney).filter((id) => ancestorIds.has(id));

        adj_list.set(nodeId, {
            // GraphData.sequenceSteps() mutates the node it walks (SegmentId, segment.Name,
            // segment.Segments[0], ...). This graph is disposable/isolated (see comment on
            // computeCurrentNodeGraphData), so it must walk a clone - not the live reactive node -
            // otherwise those writes retrigger the deep watcher in NodeConditions.vue that
            // calls back into here, looping until Vue's recursive-update guard kicks in and
            // freezing the tab.
            node: deepClone(toRaw(node)),
            to: conns_to,
            fr: conns_fr,
            path: 1,
            type_nr: 1, // Will be updated during sequencing
        });
    }

    return { adj_list };
};

export type CurrentNodeGraphData = {
    step: any | null;
    incomingSegments: any[];
};

/**
 * Computes both the target node's own step (createNewStep/updateExistingStep output) and the
 * segment conditions it inherits from the journey's ancestor nodes (the entry node's
 * lists/segments plus any intermediate Segment/Condition nodes' own segments), WITHOUT that
 * node's own segment applied. Used to preview a node's audience scoped to the journey it
 * actually belongs to, instead of the whole account.
 *
 * Both values come from a single ancestor-graph build + sequenceSteps() pass - they used to be
 * two separate functions that each rebuilt the same ancestor adjacency list and re-ran the
 * graph sequencing from scratch, which made switching to a node roughly double the work for no
 * benefit (GraphData.sequenceSteps() already computes .steps and captures link segments for
 * captureNodeId in the same traversal).
 */
export const computeCurrentNodeGraphData = (targetNodeId: number): CurrentNodeGraphData => {
    const empty: CurrentNodeGraphData = { step: null, incomingSegments: [] };

    if (!activeJourney.startingNode) return empty;

    const ancestorData = buildAncestorAdjacencyList(targetNodeId);
    if (!ancestorData) return empty;

    // A fresh, disposable graph instance - editor.segmentGraph is also used by buildGraph()/
    // stepList for a full-journey traversal, so reusing it here would let this isolated
    // preview overwrite state (.list/.steps) that the full-journey traversal relies on, or
    // vice versa.
    const tempGraph = new GraphData();
    tempGraph.captureNodeId = targetNodeId;
    tempGraph.setList(ancestorData.adj_list, activeJourney.startingNode!);
    tempGraph.sequenceSteps();

    const step = tempGraph.steps[targetNodeId] || null;

    if (targetNodeId === activeJourney.startingNode) {
        return { step, incomingSegments: [] };
    }

    const captures = tempGraph.capturedLinkSegments || [];

    // A single incoming branch: use its segments as-is, same shape as before.
    if (captures.length <= 1) {
        return { step, incomingSegments: captures[0] || [] };
    }

    // Multiple incoming branches (a merge point): a contact only needs to satisfy ONE of
    // them to have reached this node, so OR the branches together instead of picking one.
    return {
        step,
        incomingSegments: [
            {
                Name: null,
                Type: 'SegmentGroup',
                Bool: 2,
                Segments: captures.map((segments) => ({
                    Name: null,
                    Type: 'SegmentGroup',
                    Bool: 1,
                    Segments: segments,
                })),
            },
        ],
    };
};
